import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { adminAction } from '../../../redux/common/action';
import { Link } from 'react-router-dom';
import { URL } from '../../../redux/common/url';
import { AsyncPaginate } from 'react-select-async-paginate';
import { toast } from 'react-toastify';
import axios from 'axios';
import { URL as COMMON_URL } from '../../../redux/common/url';

interface Option {
    value: string;
    label: string;
}

interface PaginatedResponse {
    results: Option[];
    has_more: boolean;
}

interface Value {
    id: string;
    name: string;
}

interface ScheduledEntry {
    circleName: any;
    jenName: string;
    timestamp: string; // ISO string for when the entry was added
}

function ForceSchedule() {
    const dispatch = useDispatch();

    const [criId, setCriId] = useState<any>('');
    const [circles, setCircles] = useState<Value[]>([]);
    const [selecetedCircle, setSelecetedCircle] = useState<Value[]>([]);
    const [managerKeyJen, setManagerKeyJen] = useState<any>(0);
    const [seleceteMangerJen, setSeleceteMangerJen] = useState<any>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [scheduledEntries, setScheduledEntries] = useState<ScheduledEntry[]>([]);

    // Fetch circles on mount
    useEffect(() => {
        fetchCircles();
    }, []);

    // Fetch circles via axios
    const fetchCircles = async () => {
        try {
            const response: any = await axios.get(`${URL.API_BASE_URL + URL.getAllCircles}`);
            if (response?.data?.data) {
                setCircles(response?.data?.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    // Fetch circles via Redux (redundant, but keeping as per original code)
    useEffect(() => {
        dispatch(adminAction.getgetAllCircles(''));
        return () => { };
    }, []);

    // Reset JEN selection when circle changes
    useEffect(() => {
        if (criId) {
            loadOptionsgetManagerJen('', [], { page: 1 });
            setManagerKeyJen((prevKey: any) => prevKey + 1);
            setSeleceteMangerJen({});
        }
    }, [criId]);

    // Load JEN options dynamically
    const loadOptionsgetManagerJen = async (search: any, loadedOptions: any, { page }: { page: any }) => {
        const token: any = await localStorage.getItem('kt-auth-react-v');

        const response = await fetch(
            URL.API_BASE_URL +
            `/api/admins/get_all_jen?search=${search}&page=${page}&circle_id=${criId}`,
            {
                headers: {
                    Authorization: `Bearer ${JSON.parse(token).api_token}`,
                },
            }
        );

        const responseJSON: PaginatedResponse = await response.json();

        const options: any = responseJSON?.results?.map((data: any) => {
            data.value = data?.id;
            data.label = data?.name.toUpperCase(); // Capitalize JEN names in dropdown
            return data;
        });

        return {
            options: options,
            hasMore: responseJSON.has_more,
            additional: {
                page: page + 1,
            },
        };
    };

    // Load scheduled entries from localStorage on mount
    useEffect(() => {
        const storedEntries = localStorage.getItem('scheduledEntries');
        if (storedEntries) {
            setScheduledEntries(JSON.parse(storedEntries));
        }
    }, []);

    // Function to check if time is past 12 PM IST and clear data
    const clearDataAfter12PM = () => {
        const now = new Date('2025-05-26T16:30:00+05:30'); // Current time: 04:30 PM IST
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const isPast12PM = hours > 12 || (hours === 12 && minutes > 0);

        // Check if data was already cleared today
        const lastCleared = localStorage.getItem('lastClearedDate');
        const today = now.toISOString().split('T')[0]; // YYYY-MM-DD

        if (isPast12PM && lastCleared !== today) {
            localStorage.removeItem('scheduledEntries');
            setScheduledEntries([]);
            localStorage.setItem('lastClearedDate', today);
        }
    };

    // Check time on mount and every minute
    useEffect(() => {
        clearDataAfter12PM();
        const interval = setInterval(clearDataAfter12PM, 60 * 1000); // Check every minute
        return () => clearInterval(interval);
    }, []);

    const handleBookingSchedule = async () => {
        if (!criId) {
            toast.error('PLEASE SELECT CIRCLE FIRST');
            return;
        } else if (!seleceteMangerJen?.id) {
            toast.error('PLEASE SELECT JEN');
            return;
        };

        setIsLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem('kt-auth-react-v')!);
            const config = {
                headers: {
                    Authorization: 'Bearer ' + token.api_token,
                    'Content-Type': 'multipart/form-data',
                },
            };

            const res = await axios.get(
                `${COMMON_URL.API_BASE_URL}${COMMON_URL.forceCroneJobJen}?jen_id=${seleceteMangerJen?.id}`,
                config
            );

            if (res?.data?.status) {
                toast.success(res?.data?.message.toUpperCase());
                // Store circle and JEN names
                const circleName = circles.find((circle) => circle.id == criId) || 'UNKNOWN';
                const jenName = seleceteMangerJen?.name || 'UNKNOWN';
                const newEntry: ScheduledEntry = {
                    circleName,
                    jenName,
                    timestamp: new Date().toISOString(),
                };

                // Update scheduled entries (limit to 5)
                const updatedEntries = [newEntry, ...scheduledEntries].slice(0, 5);
                setScheduledEntries(updatedEntries);
                localStorage.setItem('scheduledEntries', JSON.stringify(updatedEntries));

                setCriId('');
                setSeleceteMangerJen({});
                setManagerKeyJen((prevKey: any) => prevKey + 1);
            } else {
                toast.error(res?.data?.message.toUpperCase());
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message?.toUpperCase() || 'ERROR COMPLETING BOOKING');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                    <div id="kt_app_toolbar_container" className="d-flex flex-stack container-xxl">
                        <div className="d-flex flex-column flex-wrap me-3 page-title">
                            <h1 className="my-0 fw-bold text-dark page-heading fs-3 uppercase">
                                CURRENT JOB
                            </h1>
                            <ul className="my-0 breadcrumb-separatorless pt-1 fw-semibold breadcrumb fs-7">
                                <li className="breadcrumb-item text-muted uppercase">
                                    <Link to={'/'} className="text-hover-primary text-muted uppercase">
                                        HOME
                                    </Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <span className="bg-gray-400 w-5px h-2px bullet"></span>
                                </li>
                                <li className="breadcrumb-item text-muted uppercase">CURRENT JOB</li>
                            </ul>
                        </div>
                        <div className="d-flex align-items-center gap-2 gap-lg-3">
                            <div className="m-0"></div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm p-4 mb-4">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label mb-2 fw-semibold fs-6 uppercase">
                                <span className="required">CIRCLE</span>
                            </label>
                            <select
                                className="bg-transparent form-control uppercase"
                                aria-label="SELECT EXAMPLE"
                                name="circle_id"
                                value={criId}
                                onChange={(e: any) => {
                                    setCriId(e?.target?.value);
                                    setSeleceteMangerJen({});
                                    setSelecetedCircle(e?.target?.value);
                                }}
                                disabled={isLoading}
                            >
                                <option value={''}>SELECT CIRCLE</option>
                                {circles?.map((data, i) => (
                                    <option value={data?.id} key={i} className="uppercase">
                                        {data?.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label mb-2 fw-semibold fs-6 uppercase">
                                <span className="required">SELECT JEN</span>
                            </label>
                            <AsyncPaginate<any, any, any>
                                key={managerKeyJen}
                                value={seleceteMangerJen}
                                loadOptions={loadOptionsgetManagerJen}
                                onChange={(e: any) => {
                                    setSeleceteMangerJen(e);
                                }}
                                additional={{
                                    page: 1,
                                }}
                                className="bg-transparent form-control uppercase"
                                isDisabled={isLoading}
                                placeholder="SELECT JEN"
                            />
                        </div>
                        <div className="col-md-2">
                            <button
                                onClick={handleBookingSchedule}
                                className="btn btn-success w-100 uppercase"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    'SCHEDULE BOOKING'
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Display Scheduled Entries */}
                {scheduledEntries.length > 0 && (
                    <div className="card border-0 shadow-sm p-4 mb-4">
                        <h2 className="text-lg font-bold mb-4 uppercase">RECENTLY SCHEDULED JOBS</h2>
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th className="uppercase">CIRCLE</th>
                                        <th className="uppercase">JEN</th>
                                        <th className="uppercase">SCHEDULED AT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scheduledEntries.map((entry, index) => (
                                        <tr key={index}>
                                            <td className="uppercase">{entry?.circleName?.name }</td>
                                            <td className="uppercase">{entry?.jenName}</td>
                                            <td className="uppercase">
                                                {new Date(entry?.timestamp).toLocaleString('en-US', {
                                                    timeZone: 'Asia/Kolkata',
                                                    hour12: true,
                                                })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ForceSchedule;